import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFilteredVirtualCards,
  selectUsersFullPaginatedDataFromState,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/YourCardsInactive/redux/yourCardsInactive.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectVirtualCardsInactiveRecipientMeIssuedCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const YourCardsInactive: React.FC = () => {
  const userId = useSelector(selectUserIdFromState)
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const { analytics } = useAnalyticsContext()
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const virtualCardsInactiveRecipientMeIssuedCount = useSelector(
    selectVirtualCardsInactiveRecipientMeIssuedCountStatisticsFromState,
  )
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )
  const appName = useSelector(selectAppName)

  const dispatch = useBackgroundDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.VirtualCardsInactiveRecipientMeIssuedCountStatistics,
    ])
  }, [dispatchStatistics])

  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    dispatch(
      postReportVirtualCardsDownload({
        ...filters,
        issued: true,
        recipient: userId,
        statuses: [
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Consumed,
          VirtualCardStatus.Closed,
          VirtualCardStatus.Expired,
        ],
      }),
    )
    analytics?.track(
      'your cards - download all virtual cards inactive - text link press',
      {
        screen: '/virtualcards',
        intent: 'download all virtual cards inactive',
        interaction: 'text link press',
      },
    )
  }

  const fetchVirtualCards = (
    filtersBody?: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        count: 10,
        page: page,
        ...filtersBody,
        issued: true,
        recipient: userId,
        statuses: [
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Consumed,
          VirtualCardStatus.Closed,
          VirtualCardStatus.Expired,
        ],
      }),
    )
  }

  const infiniteScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCards,
  }

  return !statisticsLoaded.virtualCardsInactiveRecipientMeIssuedCountStatisticsLoaded ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Your Inactive Virtual Cards | ${appName}`}</title>
        <meta
          name="description"
          content={`Your Inactive Virtual Cards page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent noStackingIndex>
        <VirtualCardsBoxAndLinks
          onDownloadClick={dispatchPostReportVirtualCardsDownload}
          defaultTitle={
            <ObjectBoxTitle
              static={{
                label: <Trns source={'Virtual Cards'} />,
                count: virtualCardsInactiveRecipientMeIssuedCount,
              }}
            />
          }
          virtualCards={filteredVirtualCards}
          emptyStateDesign={TableEmptyStateDesigns.YourVirtualCardsInactive}
          hideFilters
          infiniteScrollProps={infiniteScrollProps}
          totalCount={virtualCardsInactiveRecipientMeIssuedCount}
          usersFullPaginatedData={usersFullPaginatedData}
        />
      </BackgroundPageContent>
    </>
  )
}

export default YourCardsInactive
